import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';

const SEEN_KEY = 'bhajanmala_seen_pron_guide';

/**
 * Contextual entry to the pronunciation guide, shown near transliterated
 * content — the point of confusion. Only for English (IAST) readers.
 *
 * First time (prompt unseen): a warm one-time banner with "Show me" / "Not now".
 * After it's dismissed: a subtle, permanent "How to read this" link.
 * The primary discovery path for the guide.
 */
export const PronunciationHint: React.FC = () => {
  const selectedLanguage = useSelector((s: RootState) => s.language.selectedLanguage);
  const [seen, setSeen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SEEN_KEY) === '1';
    } catch {
      return true; // if storage is unavailable, don't nag — fall back to the quiet link
    }
  });

  if (selectedLanguage !== 'English') return null;

  const markSeen = () => {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* ignore */
    }
    setSeen(true);
  };

  if (!seen) {
    return (
      <div className="-mt-4 mb-8 rounded-xl border border-light bg-bg-2 p-4 sm:p-5 max-w-xl">
        <p className="font-display text-h3 font-semibold text-accent-gold mb-1">
          New to these accent marks?
        </p>
        <p className="text-body text-text-secondary leading-relaxed mb-4">
          Learn to read words like <span className="font-display">śrī</span> and{' '}
          <span className="font-display">gaṇapati</span> in a couple of minutes.
        </p>
        <div className="flex items-center gap-2">
          <Link
            to="/pronunciation"
            onClick={markSeen}
            className="inline-flex items-center rounded-md bg-accent-gold px-4 py-2 text-small font-semibold text-bg-0 hover:opacity-90 transition-opacity"
          >
            Show me
          </Link>
          <button
            type="button"
            onClick={markSeen}
            className="rounded-md px-3 py-2 text-small font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  return (
    <Link
      to="/pronunciation"
      className="inline-flex items-center gap-1.5 -mt-5 mb-8 text-small font-medium text-text-secondary hover:text-accent-gold transition-colors"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      How to read this
    </Link>
  );
};

export default PronunciationHint;
